import { themeAtom } from "../../../shared/atoms";
import {
  AdminCertificatesSettingsEditableField,
  AdminCertificatesSettingsFormValues,
  AdminCertificatesSettingsPayload,
  AdminCertificatesSettingsRow,
} from "../../models/admin-certificates-settings.models";
import {
  createAdminCertificatesSettingsFormValues,
  toAdminCertificatesSettingsPayload,
} from "../../utils/admin-certificates-settings.utils";
import AdminCertificatesSettingsEditableFields from "./sub-components/modal/AdminCertificatesSettingsEditableFields";
import AdminCertificatesSettingsModalActions from "./sub-components/modal/AdminCertificatesSettingsModalActions";
import AdminCertificatesSettingsModalHeader from "./sub-components/modal/AdminCertificatesSettingsModalHeader";
import { useAtomValue } from "jotai";
import { FormEvent, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  row: AdminCertificatesSettingsRow;
  vesselTypeOptions: string[];
  saving: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onSave: (payload: AdminCertificatesSettingsPayload) => void | Promise<void>;
};

const AdminCertificatesSettingsModal = ({ row, vesselTypeOptions, saving, errorMessage, onCancel, onSave }: Props) => {
  const theme = useAtomValue(themeAtom);
  const [formValues, setFormValues] = useState<AdminCertificatesSettingsFormValues>(() =>
    createAdminCertificatesSettingsFormValues(row, vesselTypeOptions),
  );
  const [validationMessage, setValidationMessage] = useState<string | null>(null);

  useEffect(() => {
    setFormValues(createAdminCertificatesSettingsFormValues(row, vesselTypeOptions));
    setValidationMessage(null);
  }, [row, vesselTypeOptions]);

  const updateField = (field: AdminCertificatesSettingsEditableField, value: string) => {
    setFormValues((current) => ({ ...current, [field]: value }));
  };

  const updateExcludedVesselTypes = (value: string[]) => {
    setFormValues((current) => ({ ...current, EXCLUDED_VESSEL_TYPES: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      const payload = toAdminCertificatesSettingsPayload(formValues);
      setValidationMessage(null);
      await onSave(payload);
    } catch (error) {
      setValidationMessage(error instanceof Error ? error.message : "Certificates settings form is invalid.");
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-certificates-settings-modal-title"
      data-test="admin-certificates-settings-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-3 py-4"
    >
      <section
        className={twMerge(
          "flex max-h-[88vh] w-full max-w-[724px] flex-col bg-white shadow-2xl dark:bg-gray-800",
          theme.home.tailwind.textDefault,
        )}
      >
        <AdminCertificatesSettingsModalHeader row={row} saving={saving} onCancel={onCancel} />

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
          data-test="admin-certificates-settings-form"
        >
          <div
            className={twMerge(
              "min-h-0 flex-1 overflow-y-auto px-5 py-4",
              "[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar]:w-2.5",
              "[&::-webkit-scrollbar-thumb]:bg-[#B7C9C4] [&::-webkit-scrollbar-track]:bg-[#F3F4F4]",
            )}
          >
            <AdminCertificatesSettingsEditableFields
              values={formValues}
              vesselTypeOptions={vesselTypeOptions}
              saving={saving}
              onChange={updateField}
              onExcludedVesselTypesChange={updateExcludedVesselTypes}
            />
          </div>

          {(validationMessage || errorMessage) && (
            <div
              role="alert"
              data-test="admin-certificates-settings-modal-error"
              className="border-t border-[#F59692] bg-[#FFEAED] px-5 py-3 text-sm text-[#BF0002]"
            >
              {validationMessage ?? errorMessage}
            </div>
          )}

          <AdminCertificatesSettingsModalActions saving={saving} onCancel={onCancel} />
        </form>
      </section>
    </div>
  );
};

export default AdminCertificatesSettingsModal;
